// Pure HTTP-header parsing + sensitive-key validation (agent.md module plan).
// Shared by the HTTP interface Headers textarea and the Pipecat Cloud
// initialization-body check: raw secrets never ride config JSON — they ride
// secret references resolved server-side. No React, no network.
import { isRecord } from "./connectionConfig";

export const sensitiveHeaderKeyParts = [
  "api_key",
  "authorization",
  "client_secret",
  "credential",
  "password",
  "private_key",
  "secret",
  "token",
] as const;

export function isSensitiveHeaderKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return sensitiveHeaderKeyParts.some((part) => normalized.includes(part));
}

// Every key anywhere in a JSON value tree is held to the same sensitive-key
// law as headers.
export function containsSensitiveJsonKey(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(containsSensitiveJsonKey);
  }
  if (!isRecord(value)) {
    return false;
  }
  return Object.entries(value).some(
    ([key, item]) =>
      isSensitiveHeaderKey(key) || containsSensitiveJsonKey(item),
  );
}

export type HeaderParseResult =
  | { error: string; ok: false }
  | { ok: true; value: Record<string, string> | null };

export function parseHeaders(value: string): HeaderParseResult {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { ok: true, value: null };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { error: "Headers must be a valid JSON object.", ok: false };
  }

  if (!isRecord(parsed)) {
    return { error: "Headers must be a valid JSON object.", ok: false };
  }

  const headers: Record<string, string> = {};
  for (const [key, item] of Object.entries(parsed)) {
    if (isSensitiveHeaderKey(key)) {
      return {
        error: "Headers must not carry raw secrets; use Secret reference.",
        ok: false,
      };
    }
    if (typeof item !== "string") {
      return { error: "Header values must be strings.", ok: false };
    }
    headers[key] = item;
  }

  return {
    ok: true,
    value: Object.keys(headers).length > 0 ? headers : null,
  };
}
